import React, { useEffect, useState, useContext } from 'react';
import { useLingui } from '@lingui/react';
import {
  theme,
  ProgressBarMulti,
  Text,
  Box,
  Spinner,
  SpinnerSize,
  useMediaQuery,
  belowBreakpoint,
  Theme,
} from '@bechtel/gds';
import { Scalars, PersonaType, PlanningWeekStats, CrewMember, UnitOfMeasure, PlanningWeek } from '@bechtel/types';
import { get, map, sortBy, minBy, maxBy, concat } from 'lodash';
import { crewMemberWeekStatMock } from './mock';
import {
  useDate,
  usePlanning,
  PlanningActionType,
  UnassignedGeneralForemanId,
  AllForemanId,
  UnassignedForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  UnassignedSuperintendentId,
  bankersRound,
  ErrorRetry,
  useName,
  useConfig,
} from '@bechtel/client-common';
import { WeekPlanStyled, WeekPlanHeaderContainer } from './styled';
import { useWeekPlanQuery } from './use-week-plan';
import { PlanningWeekCardProps } from '../../types';
import { NetworkStatus } from '@apollo/client';
import { ThemeContext } from 'styled-components';
import { t, Trans } from '@lingui/macro';

function getCrewMemberInitialWeekStats(
  planningWeekStats: PlanningWeekStats[],
  persona: PersonaType,
  personaValue: string
) {
  const crewStats: PlanningWeekStats[] = [];
  map(planningWeekStats, (item: PlanningWeekStats) => {
    const weekData = {
      ...item,
      id: Math.random().toString(),
      claimedMilestoneStats: crewMemberWeekStatMock.claimedMilestoneStats,
      plannedMilestoneStats: crewMemberWeekStatMock.plannedMilestoneStats,
      committedMilestoneStats: crewMemberWeekStatMock.committedMilestoneStats,
      target: crewMemberWeekStatMock.target,
      crewMember: {
        id:
          persona === PersonaType.SUPERINTENDENT
            ? AllSuperintendentId
            : PersonaType.GENERAL_FOREMAN
            ? AllGeneralForemanId
            : AllForemanId,
        firstName: `${persona === PersonaType.SUPERINTENDENT ? t`All` : t`Multiple`}`,
        lastName: ` ${personaValue}${persona === PersonaType.SUPERINTENDENT ? 's' : ''}`,
        persona: persona,
      },
    };
    crewStats.push(weekData);
  });
  return crewStats;
}

export const PlanningWeekCard: React.FC<PlanningWeekCardProps> = ({ planWeekStats }) => {
  const { i18n } = useLingui();
  const { numPlanningNextWeeks } = useConfig();
  const { selectedForeman, selectedGeneralForeman, selectedSuperintendent, planningDispatch } = usePlanning();
  const [
    getPlanningWeekHeader,
    {
      loading: planningWeekHeaderLoading,
      data: planningWeekHeaderData,
      error: planningWeekHeaderError,
      networkStatus: planningWeekHeaderNetworkStatus,
      refetch: planningWeekHeaderRefetch,
    },
  ] = useWeekPlanQuery();
  const [getSuperintendentWeekStats, { loading: supLoading, data: supData }] = useWeekPlanQuery();
  const [getGeneralForemanWeekStats, { loading: gfLoading, data: gfData }] = useWeekPlanQuery();
  const [getForemanWeekStats, { loading: fmLoading, data: fmData }] = useWeekPlanQuery();
  const [planningWeekHeader, setPlanningWeekHeader] = useState<PlanningWeekStats[]>([]);
  const [superintendentWeekStats, setSuperintendentWeekStats] = useState<PlanningWeekStats[]>([]);
  const [generalForemanWeekStats, setGeneralForemanWeekStats] = useState<PlanningWeekStats[]>([]);
  const [foremanWeekStats, setForemanWeekStats] = useState<PlanningWeekStats[]>([]);

  useEffect(() => {
    getPlanningWeekHeader({
      variables: {
        input: {
          numNextWeeks: numPlanningNextWeeks,
        },
      },
    });
  }, []);

  useEffect(() => {
    const planningWeekHeader = get(planningWeekHeaderData, 'planningWeekStats', []);
    if (planningWeekHeader && planningWeekHeader.length) {
      setPlanningWeekHeader(planningWeekHeader);
      const weekStartDate = minBy(planningWeekHeader, 'startDate')?.startDate;
      const weekEndDate = maxBy(planningWeekHeader, 'endDate')?.endDate;
      planningDispatch({
        type: PlanningActionType.SetPlanningWeekSchedule,
        payload: { weekStartDate, weekEndDate },
      });
    }
  }, [planningWeekHeaderData, planningDispatch]);

  useEffect(() => {
    if (
      selectedSuperintendent &&
      selectedSuperintendent.id &&
      selectedSuperintendent.persona &&
      !(selectedSuperintendent.id === AllSuperintendentId || selectedSuperintendent.id === UnassignedSuperintendentId)
    ) {
      getSuperintendentWeekStats({
        variables: {
          input: {
            persona: selectedSuperintendent.persona,
            crewMemberId: selectedSuperintendent.id,
            numNextWeeks: numPlanningNextWeeks,
          },
        },
      });
    }
  }, [selectedSuperintendent]);

  useEffect(() => {
    if (
      selectedSuperintendent?.id === AllSuperintendentId ||
      selectedSuperintendent?.id === UnassignedSuperintendentId
    ) {
      setSuperintendentWeekStats(
        getCrewMemberInitialWeekStats(
          planningWeekHeader,
          PersonaType.SUPERINTENDENT,
          i18n._(PersonaType.SUPERINTENDENT)
        )
      );
    } else {
      const supPlanningWeekStats = get(supData, 'planningWeekStats', superintendentWeekStats);
      setSuperintendentWeekStats(supPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = [...get(supData, 'planningWeekStats', [])];
    }
  }, [planningWeekHeader, selectedSuperintendent, supData]);

  useEffect(() => {
    if (
      selectedGeneralForeman &&
      selectedGeneralForeman.id &&
      selectedGeneralForeman.persona &&
      selectedGeneralForeman.id !== AllGeneralForemanId &&
      selectedGeneralForeman.id !== UnassignedGeneralForemanId
    ) {
      getGeneralForemanWeekStats({
        variables: {
          input: {
            persona: selectedGeneralForeman.persona,
            crewMemberId: selectedGeneralForeman.id,
            numNextWeeks: numPlanningNextWeeks,
          },
        },
      });
    }
  }, [selectedGeneralForeman]);

  useEffect(() => {
    if (
      selectedGeneralForeman?.id === AllGeneralForemanId ||
      selectedGeneralForeman?.id === UnassignedGeneralForemanId
    ) {
      setGeneralForemanWeekStats(
        getCrewMemberInitialWeekStats(
          planningWeekHeader,
          PersonaType.GENERAL_FOREMAN,
          i18n._(PersonaType.GENERAL_FOREMAN)
        )
      );
    } else {
      const generalForemanPlanningWeekStats = get(gfData, 'planningWeekStats', generalForemanWeekStats);
      setGeneralForemanWeekStats(generalForemanPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = concat(planWeekStats.current, get(gfData, 'planningWeekStats', []));
    }
  }, [planningWeekHeader, selectedGeneralForeman, gfData]);

  useEffect(() => {
    if (
      selectedForeman &&
      selectedForeman.id &&
      selectedForeman.persona &&
      selectedForeman.id !== AllForemanId &&
      selectedForeman.id !== UnassignedForemanId
    ) {
      getForemanWeekStats({
        variables: {
          input: {
            persona: selectedForeman.persona,
            crewMemberId: selectedForeman.id,
            numNextWeeks: numPlanningNextWeeks,
          },
        },
      });
    }
  }, [selectedForeman, getForemanWeekStats]);

  useEffect(() => {
    if (selectedForeman?.id === AllForemanId || selectedForeman?.id === UnassignedForemanId) {
      setForemanWeekStats(
        getCrewMemberInitialWeekStats(planningWeekHeader, PersonaType.FOREMAN, i18n._(PersonaType.FOREMAN))
      );
    } else {
      const foremanPlanningWeekStats = get(fmData, 'planningWeekStats', foremanWeekStats);
      setForemanWeekStats(foremanPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = concat(planWeekStats.current, get(fmData, 'planningWeekStats', []));
    }
  }, [planningWeekHeader, selectedForeman, fmData]);
  return (
    <WeekPlanHeaderContainer>
      {(planningWeekHeaderLoading || planningWeekHeaderNetworkStatus === NetworkStatus.refetch) && (
        <Box data-testid="loading" css="width:100%;height:100%;">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Spinner size={SpinnerSize.MEDIUM} />
          </Box>
        </Box>
      )}
      {!planningWeekHeaderLoading && planningWeekHeaderError && (
        <div data-testid="retry">
          <ErrorRetry
            refetch={() =>
              planningWeekHeaderRefetch({
                input: {
                  numNextWeeks: numPlanningNextWeeks,
                },
              })
            }
          >
            <Trans>There was a problem loading planning week stats. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!planningWeekHeaderLoading && !planningWeekHeaderError && (
        <WeekPlanHeader planningWeekHeaderStats={planningWeekHeader} />
      )}
      {superintendentWeekStats && superintendentWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedSuperintendent &&
            (selectedSuperintendent.id === AllSuperintendentId ||
              selectedSuperintendent.id === UnassignedSuperintendentId)
              ? superintendentWeekStats[0].crewMember
              : selectedSuperintendent
          }
          planningWeekStats={superintendentWeekStats}
          loading={supLoading}
        />
      )}
      {generalForemanWeekStats && generalForemanWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedGeneralForeman &&
            (selectedGeneralForeman.id === UnassignedGeneralForemanId ||
              selectedGeneralForeman.id === AllGeneralForemanId)
              ? generalForemanWeekStats[0].crewMember
              : selectedGeneralForeman
          }
          planningWeekStats={generalForemanWeekStats}
          loading={gfLoading}
        />
      )}
      {foremanWeekStats && foremanWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedForeman && (selectedForeman.id === UnassignedForemanId || selectedForeman.id === AllForemanId)
              ? foremanWeekStats[0].crewMember
              : selectedForeman
          }
          planningWeekStats={foremanWeekStats}
          loading={fmLoading}
        />
      )}
    </WeekPlanHeaderContainer>
  );
};

interface WeekPlanHeaderProps {
  planningWeekHeaderStats: PlanningWeekStats[];
}

const WeekPlanHeader: React.FC<WeekPlanHeaderProps> = ({ planningWeekHeaderStats }) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const { numPlanningNextWeeks } = useConfig();
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const { formatISODate } = useDate();
  const planningWeekStatsSorted = sortBy(planningWeekHeaderStats, ['year', 'weekNumber']);
  return (
    <WeekPlanStyled>
      {map(planningWeekStatsSorted, (item: PlanningWeek, index: number) => {
        let heading = t`This Week's Plan`;
        switch (index) {
          case 1:
            heading = t`Next Week's Plan`;
            break;
          case 2:
            heading = t`Two Weeks Out`;
            break;
          case 3:
            heading = t`Three Weeks Out`;
            break;
        }
        return (
          <Box
            style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
            data-testid={`sup-week-card-container-${index}`}
            key={item.id}
          >
            <Box
              data-testid="week-card-plan-wrapper"
              flexDirection={{ sm: 'column' }}
              px={{ sm: '450' }}
              pb={{ sm: '400' }}
              pt={{ sm: '500' }}
              css={`
                width: 100%;
                color: ${({ theme }) => theme.colors.grey[1000]};
              `}
            >
              <Box pb={{ sm: '300' }}>
                <Text weight="600" size={isDesktop ? '300' : '400'} family="graphik">
                  {heading}
                </Text>
              </Box>
              <Box pb={{ sm: '300' }} data-testid="week-card-number">
                <Text size={isDesktop ? '200' : '300'} weight="600">
                  <Trans>Week</Trans> {item.weekNumber}
                </Text>
                <Text size={isDesktop ? '200' : '300'} style={{ paddingLeft: `${theme.spacings[200]}` }}>
                  {item.startDate ? formatISODate(item.startDate) : t`N/A`} -{' '}
                  {item.endDate ? formatISODate(item.endDate) : t`N/A`}
                </Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </WeekPlanStyled>
  );
};

interface WeekPlanCardProps {
  crewMember: CrewMember;
  planningWeekStats: PlanningWeekStats[];
  loading: Scalars['Boolean'];
}
const WeekPlanCard: React.FC<WeekPlanCardProps> = ({ crewMember, planningWeekStats, loading }) => {
  const { numPlanningNextWeeks } = useConfig();
  const planningWeekStatsSorted = sortBy(planningWeekStats, ['year', 'weekNumber']);
  return (
    <WeekPlanStyled>
      {map(planningWeekStatsSorted, (item: PlanningWeekStats, index: number) => {
        return (
          <Box
            px={{ sm: '450' }}
            pb={{ sm: '300' }}
            style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
            data-testid={`${crewMember.persona}-week-card-container-${index}`}
            key={item.id}
          >
            <ProgressBar
              key={`${item.id}${index}${item.crewMember?.id}`}
              crewMember={crewMember}
              claimedMilestoneStats={item.claimedMilestoneStats}
              plannedMilestoneStats={item.plannedMilestoneStats}
              target={item.target}
              loading={loading}
            />
          </Box>
        );
      })}
    </WeekPlanStyled>
  );
};

interface ProgressBarProps {
  claimedMilestoneStats: PlanningWeekStats['claimedMilestoneStats'];
  plannedMilestoneStats: PlanningWeekStats['plannedMilestoneStats'];
  target: PlanningWeekStats['target'];
  crewMember?: CrewMember;
  loading: boolean;
}

interface WeekProgressDataValues {
  fillColor: string;
  label: string;
  value: number;
  labelValue?: number;
  showLegendBorder?: boolean;
  subLabel?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  claimedMilestoneStats,
  plannedMilestoneStats,
  target,
  crewMember,
  loading,
}) => {
  const { getFullName } = useName();
  const { unitOfMeasure } = usePlanning();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const data: WeekProgressDataValues[] = [];
  let showUnitPercentage = true;
  let plannedMilestoneStatsLabelValue;
  let claimedMilestoneStatsLabelValue;

  if (unitOfMeasure === UnitOfMeasure.LINEAR_FEET) {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.quantity.quantity
      ? plannedMilestoneStats?.quantity.quantity
      : 0;
    claimedMilestoneStatsLabelValue = claimedMilestoneStats?.quantity.quantity
      ? claimedMilestoneStats?.quantity.quantity
      : 0;
  } else if (unitOfMeasure === 'MILESTONE') {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.numMilestones ? plannedMilestoneStats?.numMilestones : 0;
    claimedMilestoneStatsLabelValue = claimedMilestoneStats?.numMilestones ? claimedMilestoneStats?.numMilestones : 0;
  } else {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.hours ? plannedMilestoneStats?.hours : 0;
    claimedMilestoneStatsLabelValue = claimedMilestoneStats?.hours ? claimedMilestoneStats?.hours : 0;
  }
  if (
    crewMember &&
    crewMember.id &&
    (crewMember.id === AllSuperintendentId || crewMember.id === AllGeneralForemanId || crewMember.id == AllForemanId)
  ) {
    showUnitPercentage = false;
    data.push({ label: 'N/A', value: 0, fillColor: 'white', showLegendBorder: true });
  } else {
    data.push({
      label: '',
      labelValue: bankersRound(plannedMilestoneStatsLabelValue, 0),
      value: plannedMilestoneStats && plannedMilestoneStats.hours ? plannedMilestoneStats.hours : 0,
      fillColor: 'blue.500',
      subLabel: 'To-Go',
    });
    if (claimedMilestoneStats && claimedMilestoneStats.hours)
      data.push({
        label: '',
        labelValue: bankersRound(claimedMilestoneStatsLabelValue, 0),
        value: claimedMilestoneStats.hours,
        fillColor: 'orange.500',
      });
  }
  const userLabelComponent = () => {
    return (
      <Box justifyContent={{ sm: 'space-between' }} mb={{ sm: '100' }} data-testid="progress-bar-user-name">
        <Text size="200" weight="600" color="grey.1000">
          {getFullName(crewMember?.firstName, crewMember?.lastName)}
        </Text>
        {loading && <Spinner size={SpinnerSize.SMALL} style={{ marginTop: `${theme.spacings[300]}` }} />}
      </Box>
    );
  };

  let measureUnit: Scalars['String'];
  if (unitOfMeasure === UnitOfMeasure.LINEAR_FEET) {
    measureUnit = 'LF';
  } else if (unitOfMeasure === 'MILESTONE') {
    measureUnit = 'ms';
  } else {
    measureUnit = 'h';
  }

  return (
    <Box css="width:100%" data-testid="progress-bar">
      <ProgressBarMulti
        data={data}
        label={userLabelComponent}
        totalValue={target && target.hours ? target.hours : 0}
        units={showUnitPercentage ? measureUnit : ''}
        showPercentage={!isDesktop ? showUnitPercentage : false}
        bgColor="white"
        borderColor="grey.300"
        labelColor="grey.600"
      />
    </Box>
  );
};
